import { useTranslation } from 'react-i18next';

import { Button, Container, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { handleSubmitError } from '@utils/forms';

import { usePasswordResetRequestMutation } from '@api/passwordReset.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useGRecaptcha from '@hooks/useGRecaptcha';

export default function PasswordResetRequest() {
  const { t } = useTranslation();

  // ==========================================================================
  // General
  // ==========================================================================
  const { getRecaptchaToken } = useGRecaptcha();

  const [params, setParams] = useQueryParams();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
  };

  const form = useForm({
    initialValues,
  });

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('PasswordResetRequest');

      await passwordResetRequest({ body: values, recaptchaToken }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('passwordReset.successMessage'));
      setParams(params);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Api
  // ==========================================================================
  const [passwordResetRequest, { isLoading }] =
    usePasswordResetRequestMutation();

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('passwordReset.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('passwordReset.alreadyKnowPassword')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('passwordReset.signIn')}
        </Text>
      </Text>

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Text ta="center" size="sm" mb="lg" mt="xl">
          {t('passwordReset.description')}
        </Text>
        <TextInput
          label={t('passwordReset.email')}
          placeholder={t('passwordReset.emailPlaceholder')}
          required
          {...form.getInputProps('email')}
        />

        {form.errors.general && (
          <Text ta="center" c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}
        <Button
          type="submit"
          fullWidth
          mt="xl"
          style={{ display: 'block' }}
          loading={isLoading}
        >
          {t('passwordReset.resetPassword')}
        </Button>
      </form>
    </Container>
  );
}
